import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import FilterDropdown from '../FilterDropdown';
import FilterPill from '../FilterPill';
import {
  ALL_JOB_STATUSES,
  buildMultiChipLabel,
  JOB_STATUS_LABELS,
} from '../helpers';

import { JobStatusEnum } from '@/types/graphql';

type Props = {
  value: JobStatusEnum[] | undefined;
  onApply: (next: JobStatusEnum[] | undefined) => void;
  onClear: () => void;
};

/** Treat undefined or full-selection as "no filter". */
const normalize = (value: JobStatusEnum[] | undefined): Set<JobStatusEnum> => {
  if (!value || value.length === 0) return new Set(ALL_JOB_STATUSES);
  return new Set(value);
};

const StatusPill = ({ value, onApply, onClear }: Props) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState<Set<JobStatusEnum>>(() =>
    normalize(value),
  );

  // Re-sync staged from applied each time the dropdown opens.
  useEffect(() => {
    if (open) setStaged(normalize(value));
  }, [open, value]);

  const allSelected = staged.size === ALL_JOB_STATUSES.length;

  const toggleItem = (s: JobStatusEnum) => {
    setStaged((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const toggleAll = () => {
    setStaged(allSelected ? new Set() : new Set(ALL_JOB_STATUSES));
  };

  const apply = () => {
    // Both 0 and N collapse to "no filter" (undefined).
    if (staged.size === 0 || staged.size === ALL_JOB_STATUSES.length) {
      onApply(undefined);
    } else {
      onApply(ALL_JOB_STATUSES.filter((s) => staged.has(s)));
    }
    setOpen(false);
  };

  const appliedSet = normalize(value);
  const isActive =
    value !== undefined &&
    value.length > 0 &&
    appliedSet.size < ALL_JOB_STATUSES.length;
  const selectedLabels = ALL_JOB_STATUSES.filter((s) => appliedSet.has(s)).map(
    (s) => JOB_STATUS_LABELS[s],
  );
  const { label, more } = buildMultiChipLabel(selectedLabels);

  return (
    <div className="pill-wrap" ref={wrapRef}>
      <FilterPill
        placeholder="All status"
        chipLabel={isActive ? label : undefined}
        chipMore={isActive ? more : undefined}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onClear={isActive ? onClear : undefined}
        a11yLabel="Filter by status"
      />
      <FilterDropdown
        open={open}
        onClose={() => setOpen(false)}
        onApply={apply}
        anchorRef={wrapRef}
        panelStyle={{ minWidth: 200 }}
      >
        <div
          className="dropdown-option"
          role="checkbox"
          aria-checked={allSelected}
          onClick={toggleAll}
        >
          <span
            className={classNames('dropdown-check', { checked: allSelected })}
          >
            {allSelected ? <FontAwesomeIcon icon={faCheck} fixedWidth /> : null}
          </span>
          <span>{allSelected ? 'Unselect All' : 'Select All'}</span>
        </div>
        {ALL_JOB_STATUSES.map((s) => {
          const checked = staged.has(s);
          return (
            <div
              key={s}
              className="dropdown-option"
              role="checkbox"
              aria-checked={checked}
              onClick={() => toggleItem(s)}
            >
              <span className={classNames('dropdown-check', { checked })}>
                {checked ? <FontAwesomeIcon icon={faCheck} fixedWidth /> : null}
              </span>
              {JOB_STATUS_LABELS[s]}
            </div>
          );
        })}
      </FilterDropdown>
    </div>
  );
};

export default StatusPill;
